import { FiX, FiRotateCcw, FiAlertTriangle } from "react-icons/fi";

/**
 * Generic confirm dialog used for Restore / Archive actions on customers
 * and locations.
 *
 * variant:
 *   - "restore" (default): blue icon + blue confirm button
 *   - "archive": red icon + red confirm button
 *
 * details (optional): array of { label, value } pairs rendered inside a
 * highlighted info box (e.g. Customer name + number of locations).
 */
function RestoreDialog({
  variant = "restore",
  title = "Restore Location",
  description = "Restore this location and make it available again for future records?",
  confirmLabel = "Restore Location",
  details,
  onConfirm,
  onCancel,
}) {
  const isArchive = variant === "archive";
  const Icon = isArchive ? FiAlertTriangle : FiRotateCcw;

  return (
    <div onClick={onCancel} className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-[512px] max-w-full bg-white rounded-[16px] shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] p-6 flex flex-col gap-5"
      >
        <FiX
          size={16}
          className="absolute right-4 top-4 text-[#737373] cursor-pointer"
          onClick={onCancel}
        />

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <div
              className={`w-[40px] h-[40px] rounded-full flex items-center justify-center ${
                isArchive ? "bg-[#FEF2F2]" : "bg-[#F5F9FF]"
              }`}
            >
              <Icon size={20} className={isArchive ? "text-[#B91C1C]" : "text-blueColor"} />
            </div>
            <h2 className="text-[20px] leading-[24px] font-OpenSansSemiBold text-[#1A2332] capitalize">
              {title}
            </h2>
          </div>
          {Array.isArray(description) ? (
            <div className="flex flex-col gap-4">
              {description.map((paragraph, i) => (
                <p key={i} className="text-[16px] leading-[24px] text-[#707580] font-OpenSansRegular">
                  {paragraph}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-[16px] leading-[24px] text-[#707580] font-OpenSansRegular">{description}</p>
          )}
        </div>

        {Array.isArray(details) && details.length > 0 && (
          <div className="p-4 bg-[#F8F9FE] rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3] flex items-start gap-4">
            {details.map(({ label, value }, i) => (
              <div key={i} className="flex-1 flex flex-col gap-1">
                <span className="text-[12px] leading-[18px] tracking-[0.12px] uppercase font-OpenSansSemiBold text-[#7C829A]">
                  {label}
                </span>
                <span className="text-[14px] leading-[20px] tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332]">
                  {value}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-4 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-[40px] px-4 rounded-[8px] bg-white border border-[#C9CEE2] shadow-[0px_1px_2px_rgba(16,24,40,0.05)] text-[16px] leading-[24px] font-OpenSansSemiBold text-[#1A2332] capitalize"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`flex-1 h-[40px] px-4 rounded-[8px] text-white text-[16px] leading-[24px] font-OpenSansSemiBold capitalize ${
              isArchive ? "bg-[#EF4444]" : "bg-blueColor"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RestoreDialog;
